/* [2.6] Sticky add-to-cart – Nautisk Portal
   - Single-variant products: shows whenever the main "Læg i kurv" button is out of view (above OR below), so the
     first phone screen already has price + button. Products with options: only once the main button has been
     scrolled ABOVE the viewport, so nobody adds a size or colour they never saw. [UX-2026-10 P1-1]
   - Hides while the footer is visible, a drawer/modal/search/menu is open, or the Shopify cookie banner is visible.
   - The button is a native submit for form#product-form-{section}; product-form.js handles the AJAX add + cart drawer.
   - Mirrors the main button (disabled/label/loading), the price block and the form's error message [P1-2];
     follows variant:change for the image.
   - Sold out: the button becomes "Giv mig besked" and opens the notify form instead of staying a grey dead end [P2-2]. */
if (!customElements.get('np-sticky-atc')) {
  class NpStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.mainButton = this.form ? this.form.querySelector('[type="submit"]') : null;
      this.button = this.querySelector('[data-np-sticky-button]');
      this.label = this.querySelector('[data-np-sticky-label]');
      this.spinner = this.querySelector('[data-np-sticky-spinner]');
      this.priceEl = this.querySelector('[data-np-sticky-price]');
      this.errorEl = this.querySelector('[data-np-sticky-error]');
      this.img = this.querySelector('[data-np-sticky-img]');
      if (!this.form || !this.mainButton || !this.button) return;

      this.single = this.dataset.single === 'true';
      this.notifyMode = false;
      this.pastButton = false;
      this.footerVisible = false;
      this.isShown = null;
      this.update = this.update.bind(this);
      this.syncButton = this.syncButton.bind(this);
      this.syncPrice = this.syncPrice.bind(this);
      this.syncError = this.syncError.bind(this);
      this.onButtonClick = this.onButtonClick.bind(this);
      this.button.addEventListener('click', this.onButtonClick);

      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.mainButton) {
            this.pastButton = !entry.isIntersecting && (this.single || entry.boundingClientRect.top < 0);
          } else {
            this.footerVisible = entry.isIntersecting;
          }
        });
        this.update();
      });
      this.io.observe(this.mainButton);
      const footer = document.querySelector('.shopify-section-group-footer-group');
      if (footer) this.io.observe(footer);

      this.buttonMo = new MutationObserver(this.syncButton);
      this.buttonMo.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
        characterData: true,
      });

      // The stock block is re-rendered on variant change; sold out → "Giv mig besked"
      const inventory = document.getElementById(`Inventory-${this.sectionId}`);
      if (inventory) {
        this.inventoryMo = new MutationObserver(this.syncButton);
        this.inventoryMo.observe(inventory, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      }

      const price = document.getElementById(`price-${this.sectionId}`);
      if (price) {
        this.priceMo = new MutationObserver(this.syncPrice);
        this.priceMo.observe(price, { childList: true, subtree: true, characterData: true });
      }

      const productForm = this.form.closest('product-form');
      this.errorWrap = productForm ? productForm.querySelector('.product-form__error-message-wrapper') : null;
      if (this.errorWrap && this.errorEl) {
        this.errorMo = new MutationObserver(this.syncError);
        this.errorMo.observe(this.errorWrap, {
          attributes: true,
          attributeFilter: ['hidden'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      // Body class changes (drawer/modal/search) and injected nodes (cookie banner).
      this.bodyMo = new MutationObserver(this.update);
      this.bodyMo.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      document.addEventListener('visitorConsentCollected', this.update);

      this.onVariantChange = (event) => {
        const detail = event.detail || {};
        if (String(detail.sectionId) !== String(this.sectionId)) return;
        const media = detail.variant && detail.variant.featured_media;
        const src = media && media.preview_image && media.preview_image.src;
        if (this.img && src) {
          this.img.removeAttribute('srcset');
          this.img.src = `${src}${src.includes('?') ? '&' : '?'}width=120`;
        }
      };
      document.addEventListener('variant:change', this.onVariantChange);

      this.syncButton();
      this.syncPrice();
      this.syncError();
      this.update();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.buttonMo) this.buttonMo.disconnect();
      if (this.inventoryMo) this.inventoryMo.disconnect();
      if (this.priceMo) this.priceMo.disconnect();
      if (this.errorMo) this.errorMo.disconnect();
      if (this.bodyMo) this.bodyMo.disconnect();
      if (this.button) this.button.removeEventListener('click', this.onButtonClick);
      document.removeEventListener('visitorConsentCollected', this.update);
      if (this.onVariantChange) document.removeEventListener('variant:change', this.onVariantChange);
      document.body.classList.remove('np-sticky-atc-on');
    }

    cookieBannerOpen() {
      const banner = document.getElementById('shopify-pc__banner');
      if (!banner) return false;
      const style = window.getComputedStyle(banner);
      return style.display !== 'none' && style.visibility !== 'hidden' && banner.getBoundingClientRect().height > 0;
    }

    update() {
      const show = this.pastButton && !this.footerVisible && !this.cookieBannerOpen();
      if (show === this.isShown) return;
      this.isShown = show;
      this.classList.toggle('is-visible', show);
      this.toggleAttribute('inert', !show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('np-sticky-atc-on', show);
      if (show) this.measure();
    }

    measure() {
      document.documentElement.style.setProperty('--np-sticky-atc-h', `${this.offsetHeight}px`);
    }

    notifyTarget() {
      const inventory = document.getElementById(`Inventory-${this.sectionId}`);
      if (!inventory || inventory.classList.contains('visibility-hidden')) return null; // combination does not exist
      if (!inventory.querySelector('[data-np-stock-state="out"]')) return null;
      // Already signed up (form posted): nothing left to offer
      if (inventory.querySelector('.np-notify__success')) return null;
      return inventory.querySelector('.np-notify') || inventory;
    }

    syncButton() {
      const busy =
        this.mainButton.classList.contains('loading') || this.mainButton.getAttribute('aria-disabled') === 'true';
      const disabled = this.mainButton.hasAttribute('disabled');
      this.notifyMode = disabled && !!this.notifyTarget();
      this.button.disabled = disabled && !this.notifyMode;
      this.button.type = this.notifyMode ? 'button' : 'submit';
      this.button.classList.toggle('np-sticky-atc__button--notify', this.notifyMode);
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.button.classList.toggle('loading', busy);
      if (this.spinner) this.spinner.classList.toggle('hidden', !busy);
      const text = this.mainButton.querySelector('span');
      if (this.label) {
        if (this.notifyMode && this.dataset.labelNotify) this.label.textContent = this.dataset.labelNotify;
        else if (text) this.label.textContent = text.textContent.trim();
      }
    }

    onButtonClick(event) {
      if (!this.notifyMode) return;
      event.preventDefault();
      const target = this.notifyTarget();
      if (!target) return;
      const details = target.querySelector('details');
      if (details) details.open = true;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      const input = target.querySelector('input[type="email"]');
      if (input) input.focus({ preventScroll: true });
    }

    syncPrice() {
      const wrap = document.getElementById(`price-${this.sectionId}`);
      if (!wrap || !this.priceEl) return;
      const pending = wrap.querySelector('.np-price-pending');
      if (pending) {
        this.priceEl.textContent = pending.textContent.trim();
        return;
      }
      const box = wrap.querySelector('.price');
      if (!box) return;
      const onSale = box.classList.contains('price--on-sale');
      const el = box.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      if (el) this.priceEl.textContent = el.textContent.trim();
    }

    syncError() {
      if (!this.errorWrap || !this.errorEl) return;
      const message = this.errorWrap.querySelector('.product-form__error-message');
      const text = message ? message.textContent.trim() : '';
      const visible = !this.errorWrap.hasAttribute('hidden') && text !== '';
      this.errorEl.textContent = visible ? text : '';
      this.errorEl.hidden = !visible;
      if (this.isShown) this.measure();
    }
  }
  customElements.define('np-sticky-atc', NpStickyAtc);
}
